import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { syncEpisodeDateToVisit } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  type EpisodeEditImpactWarnings,
  parseEpisodeEditImpactWarnings,
} from '../../utils/episodeEditImpact';
import { formatDateForDisplay } from '../../utils/dateUtils';
import type { EpisodeBoundary } from '../../utils/episodeBoundaryVisits';
import { EpisodeEditImpactList } from './EpisodeEditImpactList';

export type EpisodeDateSyncDialogProps = {
  open: boolean;
  boundary: EpisodeBoundary;
  episodeId: string;
  visitId: string;
  /** The new visit date the user picked, ISO "YYYY-MM-DD". */
  newDate: string;
  /** The current episode boundary date (before the change), ISO "YYYY-MM-DD". */
  currentBoundaryDate: string;
  /** Current episode start date (before any change), ISO "YYYY-MM-DD". */
  episodeStartDate: string | null;
  /** Current episode end date (before any change), ISO "YYYY-MM-DD". */
  episodeEndDate: string | null;
  /** Short visit label for copy (e.g. "SOC"); falls back to "This visit". */
  visitLabel?: string;
  onClose: () => void;
  /** Called after the episode and visit were both persisted so the parent can refresh. */
  onCompleted: () => void;
  /**
   * Move only the visit, leaving the episode dates unchanged. Offered only when
   * the new date still falls inside the current episode window (otherwise a
   * visit-only reschedule would land outside the window and be rejected).
   */
  onUpdateVisitOnly: () => void;
};

const boundaryWord = (boundary: EpisodeBoundary): string =>
  boundary === 'start' ? 'start' : 'end';

/**
 * Offered when a start-defining (SOC/REC) or end-defining (DC/TRN/DTH) visit is
 * moved off the episode boundary it currently matches. Confirming calls the
 * dedicated endpoint that moves the episode boundary and the visit together in
 * one transaction, so the visit never has to pass through a window it is about
 * to change. The endpoint reuses the standard episode rules, so the 60-day cap,
 * overlap check, and stranded-record impact confirmation all still apply.
 *
 * When the new date still falls inside the current episode window, the clinician
 * can instead move only the visit and leave the episode dates alone; that option
 * is hidden when the date falls outside the window, since a visit-only reschedule
 * there would be rejected for landing outside the episode.
 */
const EpisodeDateSyncDialog: React.FC<EpisodeDateSyncDialogProps> = ({
  open,
  boundary,
  episodeId,
  visitId,
  newDate,
  currentBoundaryDate,
  episodeStartDate,
  episodeEndDate,
  visitLabel,
  onClose,
  onCompleted,
  onUpdateVisitOnly,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [saving, setSaving] = useState(false);
  const [pendingImpact, setPendingImpact] =
    useState<EpisodeEditImpactWarnings | null>(null);

  // Reset transient state whenever a fresh offer is opened.
  useEffect(() => {
    if (open) {
      setPendingImpact(null);
      setSaving(false);
    }
  }, [open, visitId, newDate]);

  const submit = useCallback(
    async (acknowledgeWarnings: boolean) => {
      setSaving(true);
      try {
        await syncEpisodeDateToVisit({
          path: { episode_id: episodeId },
          body: { visit_id: visitId, scheduled_date: newDate },
          query: acknowledgeWarnings
            ? { acknowledge_warnings: true }
            : undefined,
          // Resolve-on-4xx is the client default; opt into throwing so the 409
          // impact and 400 rule failures reach the catch below.
          throwOnError: true,
        });
      } catch (error) {
        const impact = parseEpisodeEditImpactWarnings(error);
        if (impact) {
          setPendingImpact(impact);
          setSaving(false);
          return;
        }
        console.error('Failed to sync episode date to visit:', error);
        // Nothing changed when the edit is rejected (for example the 60-day
        // cap); the dialog stays open so the user can adjust or cancel.
        showError(
          'Update Failed',
          getBusinessErrorMessage(error) ??
            'Unable to update the episode dates. Please try again.'
        );
        setSaving(false);
        return;
      }
      // Both the episode and the visit moved atomically. Let the parent refresh
      // and close; the dialog unmounts, so no state is touched after this point.
      showSuccess('Dates Updated', 'Episode and visit dates were updated.');
      onCompleted();
      onClose();
    },
    [episodeId, visitId, newDate, showSuccess, showError, onCompleted, onClose]
  );

  const word = boundaryWord(boundary);
  const label = visitLabel ?? 'This visit';

  // ISO "YYYY-MM-DD" strings compare correctly lexicographically, so no Date
  // parsing is needed. The window is the current (unchanged) episode range; a
  // visit-only move is only valid — and only offered — when the new date lands
  // inside it. The impact confirmation sub-view is about the episode-date change
  // specifically, so the visit-only option is not shown there.
  const withinEpisode =
    episodeStartDate !== null &&
    episodeEndDate !== null &&
    newDate >= episodeStartDate &&
    newDate <= episodeEndDate;
  const showUpdateVisitOnly = !pendingImpact && withinEpisode;

  return (
    <Dialog
      open={open}
      onOpenChange={(openState: boolean) => !openState && onClose()}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Update episode {word} date?</DialogTitle>
          <DialogDescription className='sr-only'>
            Offer to move the episode {word} date to match the moved visit.
          </DialogDescription>
        </DialogHeader>

        {pendingImpact ? (
          <div className='space-y-3' data-testid='episode-date-sync-impact'>
            <p className='text-sm font-medium'>
              This change affects existing records
            </p>
            <EpisodeEditImpactList warnings={pendingImpact} />
            <p className='text-sm text-muted-foreground'>
              These records are not modified. Do you want to proceed?
            </p>
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {label} is on the episode {word} date (
            {formatDateForDisplay(currentBoundaryDate)}). Move the episode{' '}
            {word} date to {formatDateForDisplay(newDate)} to match?
          </p>
        )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {showUpdateVisitOnly && (
            <Button
              type='button'
              variant='outline'
              onClick={onUpdateVisitOnly}
              disabled={saving}
              data-testid='episode-date-sync-visit-only'
            >
              Update just the target date
            </Button>
          )}
          <Button
            type='button'
            onClick={() => void submit(pendingImpact !== null)}
            disabled={saving}
          >
            {saving
              ? 'Saving…'
              : pendingImpact
                ? 'Confirm'
                : 'Update episode dates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EpisodeDateSyncDialog;
